import { useEffect } from 'react'
import { isDemoMode } from '../lib/demo/flag'

/** Floating "Live interactive demo" pill rendered at the bottom of every
 *  page in the public Guardian demo. Includes an Expand button that
 *  asks the parent (HubSpot iframe host) to swap to fullscreen via
 *  postMessage, falling back to opening the demo URL in a new tab when
 *  the demo is loaded standalone (not iframed). */
export function DemoBanner() {
  useEffect(() => {
    if (!isDemoMode || typeof window === 'undefined') return
    if (window.self === window.top) return
    const post = (kind: string, extra?: Record<string, unknown>) => {
      try {
        window.parent.postMessage({ source: 'iuvo-demo', kind, ...extra }, '*')
      } catch {
        /* parent may block — silent */
      }
    }
    post('ready', { height: document.body.scrollHeight })
    const onResize = () => post('resize', { height: document.body.scrollHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  if (!isDemoMode) return null

  const onExpand = () => {
    if (window.self !== window.top) {
      window.parent.postMessage({ source: 'iuvo-demo', kind: 'expand' }, '*')
    } else {
      window.open(window.location.href, '_blank', 'noopener')
    }
  }

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2 rounded-full"
      style={{
        background: 'rgba(15, 23, 42, 0.94)',
        color: 'rgba(255,255,255,0.92)',
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '0.02em',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(116, 212, 154, 0.32)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.32)',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6, height: 6, borderRadius: '50%',
          background: '#74d49a',
          boxShadow: '0 0 0 3px rgba(116, 212, 154,0.20)',
          display: 'inline-block',
        }}
      />
      <span>Live interactive demo · sample data</span>
      <span style={{ color: 'rgba(255,255,255,0.32)' }}>·</span>
      <button
        type="button"
        onClick={onExpand}
        className="inline-flex items-center gap-1 transition-colors hover:text-white"
        style={{ color: 'rgba(255,255,255,0.78)', background: 'transparent', border: 0, font: 'inherit', padding: 0, cursor: 'pointer' }}
      >
        Expand
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M9 2h5v5M14 2L8.5 7.5M7 14H2V9M2 14l5.5-5.5"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  )
}
